import { FunctionComponent, useState } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { faCheck, faRightToBracket } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";
import { useAppSelector } from "@/redux/hooks";

import {
	useGetCompanySendErrorsQuery,
	useResolveSendErrorMutation,
} from "@/pages/Private/redux/support-dashboard/support-dashboard.api";
import { supportDashboardSelector } from "@/pages/Private/redux/support-dashboard/support-dashboard.slice";

import { SupportCompany, SupportSendError } from "../schema/support-dashboard";
import { useLoginAsAndNavigate } from "../hooks/useLoginAsAndNavigate";
import { ErrorJsonViewer } from "./ErrorJsonViewer";

interface DeliveryErrorsPanelProps {
	company: SupportCompany;
}

const STEP_VISUAL: Record<string, { abbr: string; classes: string }> = {
	emailNode: { abbr: "@", classes: "bg-status-info-light text-status-info" },
	linkedinInviteNode: { abbr: "in", classes: "bg-status-running-light text-status-running" },
	linkedinNode: { abbr: "in", classes: "bg-status-success-light text-status-success" },
	linkedinInmailNode: { abbr: "IM", classes: "bg-status-warning-light text-status-warning" },
};

const getStepVisual = (stepType: string): { abbr: string; classes: string } =>
	STEP_VISUAL[stepType] ?? { abbr: "·", classes: "bg-inactive-item text-text-meta" };

const formatWhen = (iso: string): string => {
	try {
		return formatDistanceToNow(new Date(iso), { addSuffix: true });
	} catch {
		return iso;
	}
};

const CategoryChip: FunctionComponent<{ error: SupportSendError; t: TFunction }> = ({
	error,
	t,
}) => {
	if (error.isInmailUndelivered) {
		return (
			<span className="inline-flex items-center rounded-full bg-status-warning-light px-2.5 py-0.5 text-xs font-medium text-status-warning">
				{t("supportDashboard.deliveryErrors.inmailMismatch")}
			</span>
		);
	}

	return (
		<span
			className={classNames(
				"inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
				error.needsAttention
					? "bg-status-error-light text-status-error"
					: "bg-inactive-item text-text-meta"
			)}
		>
			{error.category}
		</span>
	);
};

interface SendErrorRowProps {
	error: SupportSendError;
	companyId: number;
	isLoading: boolean;
	isResolving: boolean;
	onLogin: (companyId: number, listId: number) => void;
	onResolve: (scheduleId: number) => void;
	t: TFunction;
}

const SendErrorRow: FunctionComponent<SendErrorRowProps> = ({
	error,
	companyId,
	isLoading,
	isResolving,
	onLogin,
	onResolve,
	t,
}) => {
	const visual = getStepVisual(error.stepType);
	const subtitle = [error.prospectTitle, error.organizationName].filter(Boolean).join(" · ");
	const listLine = [error.listName, error.campaignName].filter(Boolean).join(" › ");

	return (
		<div
			className={classNames(
				"grid grid-cols-1 gap-3 rounded-xl border border-border-light border-l-[3px] bg-brand-white px-4 py-3",
				"lg:grid-cols-[minmax(160px,1.3fr)_minmax(150px,1.3fr)_minmax(200px,2fr)_auto_auto] lg:items-center lg:gap-4",
				error.needsAttention ? "border-l-status-error" : "border-l-status-paused"
			)}
		>
			{/* Prospect */}
			<div className="min-w-0">
				<p className="truncate text-sm font-semibold text-text">
					{error.prospectName || t("supportDashboard.deliveryErrors.noName")}
				</p>
				{subtitle && <p className="truncate text-[11px] text-text-meta">{subtitle}</p>}
			</div>

			{/* Step + list/campaign */}
			<div className="min-w-0">
				<div className="flex items-center gap-2">
					<span
						className={classNames(
							"flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md text-[10px] font-semibold",
							visual.classes
						)}
					>
						{visual.abbr}
					</span>
					<span className="text-sm text-text">{error.stepLabel}</span>
				</div>
				{listLine && <p className="mt-0.5 truncate text-[11px] text-text-meta">{listLine}</p>}
			</div>

			{/* Category + reason */}
			<div className="min-w-0">
				<CategoryChip error={error} t={t} />
				<p className="mt-1 break-words text-[11px] text-text-meta">{error.reason}</p>
				{error.rawError && <ErrorJsonViewer rawError={error.rawError} t={t} />}
			</div>

			{/* When */}
			<span className="whitespace-nowrap text-xs text-text-meta lg:text-right">
				{formatWhen(error.failedAt)}
			</span>

			{/* Action */}
			<div className="flex flex-wrap gap-2 lg:justify-self-end">
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					disabled={isLoading}
					image={<Icon className="mr-1.5" icon={faRightToBracket} />}
					isLoading={isLoading}
					size={ButtonSize.S}
					title={t("supportDashboard.deliveryErrors.loginAndOpen")}
					onClick={(e) => {
						e.stopPropagation();
						onLogin(companyId, error.listId);
					}}
				/>
				<Button
					color={ButtonColor.SUCCESS}
					disabled={isResolving}
					image={<Icon className="mr-1.5" icon={faCheck} />}
					isLoading={isResolving}
					size={ButtonSize.S}
					title={t("supportDashboard.deliveryErrors.markFixed")}
					onClick={(e) => {
						e.stopPropagation();
						onResolve(error.id);
					}}
				/>
			</div>
		</div>
	);
};

export const DeliveryErrorsPanel: FunctionComponent<DeliveryErrorsPanelProps> = ({ company }) => {
	const { t } = useTranslation();
	const { loginAsAndNavigate, isLoading, error: loginError } = useLoginAsAndNavigate();

	const { sendErrorRange, sendErrorsByCompany } = useAppSelector(supportDashboardSelector);
	const { isFetching } = useGetCompanySendErrorsQuery({
		companyId: company.id,
		range: sendErrorRange,
	});
	const [resolveSendError] = useResolveSendErrorMutation();
	const [resolvingId, setResolvingId] = useState<number | null>(null);

	const handleResolve = async (scheduleId: number) => {
		setResolvingId(scheduleId);

		try {
			await resolveSendError({ scheduleId }).unwrap();
		} finally {
			setResolvingId(null);
		}
	};

	const cached = sendErrorsByCompany[company.id];
	const errors = cached ?? [];
	const isInitialLoad = cached === undefined && isFetching;

	const { needsAttention, recipientSide, inmailUndelivered } = company.sendErrors;

	return (
		<div className="mt-4 border-t border-border-light pt-4">
			<div className="mb-2 flex flex-wrap items-center gap-2">
				<h4 className="text-sm font-semibold text-status-error">
					{t("supportDashboard.deliveryErrors.title")}
				</h4>
				<span className="text-xs text-text-meta">
					{t("supportDashboard.deliveryErrors.summary", {
						total: company.sendErrors.total,
						needsAttention,
						inmailUndelivered,
					})}
				</span>
			</div>

			<div className="mb-3 flex flex-wrap gap-4 text-[11px] text-text-meta">
				<span className="inline-flex items-center gap-1.5">
					<span className="h-2 w-2 rounded-full bg-status-error" />
					{t("supportDashboard.deliveryErrors.legendAttention")}
				</span>
				<span className="inline-flex items-center gap-1.5">
					<span className="h-2 w-2 rounded-full bg-status-paused" />
					{t("supportDashboard.deliveryErrors.legendRecipient")} ({recipientSide})
				</span>
			</div>

			{loginError && <p className="mb-2 text-sm text-status-error">{loginError}</p>}

			{isInitialLoad ? (
				<p className="text-sm text-text-meta">{t("supportDashboard.deliveryErrors.loading")}</p>
			) : errors.length === 0 ? (
				<p className="text-sm text-text-meta">{t("supportDashboard.deliveryErrors.empty")}</p>
			) : (
				<div className="flex flex-col gap-2">
					{errors.map((err) => (
						<SendErrorRow
							key={err.id}
							companyId={company.id}
							error={err}
							isLoading={isLoading}
							isResolving={resolvingId === err.id}
							onLogin={loginAsAndNavigate}
							onResolve={handleResolve}
							t={t}
						/>
					))}
				</div>
			)}
		</div>
	);
};
